import React, { useState, useRef, useMemo } from "react";
import { useTranslation } from "react-i18next";
import "./ProductInstalled.css";

/* ===== Iconos SVG Mejorados ===== */
const Icon = {
  Star: (props) => (
    <svg viewBox="0 0 24 24" width="18" height="18" fill="none" {...props}>
      <path
        d="M12 3.5l2.7 5.46 6.03.88-4.36 4.25 1.03 6.02L12 17.8 6.6 20.1l1.03-6.02L3.27 9.84l6.03-.88L12 3.5Z"
        stroke="currentColor"
        strokeWidth="1.6"
        fill="currentColor"
        opacity=".85"
      />
    </svg>
  ),
  Grid: (props) => (
    <svg viewBox="0 0 24 24" width="18" height="18" fill="none" {...props}>
      <rect x="3" y="3" width="8" height="8" rx="1.5" stroke="currentColor" strokeWidth="1.6" />
      <rect x="13" y="3" width="8" height="8" rx="1.5" stroke="currentColor" strokeWidth="1.6" />
      <rect x="3" y="13" width="8" height="8" rx="1.5" stroke="currentColor" strokeWidth="1.6" />
      <rect x="13" y="13" width="8" height="8" rx="1.5" stroke="currentColor" strokeWidth="1.6" />
    </svg>
  ),
  Sliders: (props) => (
    <svg viewBox="0 0 24 24" width="18" height="18" fill="none" {...props}>
      <circle cx="8" cy="8" r="2.2" stroke="currentColor" strokeWidth="1.6" />
      <path
        d="M3 8h3M10 8h11M3 16h7M14 16h7"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
      />
      <circle cx="12" cy="16" r="2.2" stroke="currentColor" strokeWidth="1.6" />
    </svg>
  ),
  Palette: (props) => (
    <svg viewBox="0 0 24 24" width="18" height="18" fill="none" {...props}>
      <path
        d="M12 3C7.03 3 3 6.76 3 11.4c0 3.3 2.74 4.6 4.6 4.6h1.15c.78 0 1.25.9.8 1.56A2.9 2.9 0 0 0 12.2 21c5.13 0 8.8-3.78 8.8-8.2C21 6.85 16.97 3 12 3Z"
        stroke="currentColor"
        strokeWidth="1.6"
      />
      <circle cx="8.2" cy="9" r="1.1" fill="currentColor" />
      <circle cx="12" cy="7.6" r="1.1" fill="currentColor" />
      <circle cx="15.7" cy="9.3" r="1.1" fill="currentColor" />
    </svg>
  ),
  ArrowRight: (props) => (
    <svg viewBox="0 0 24 24" width="20" height="20" fill="none" {...props}>
      <path
        d="M5 12H19M19 12L12 5M19 12L12 19"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  ),
};

/* =========================
   Helpers de texturas (auto)
========================= */

const normalizeName = (name) =>
  String(name || "")
    .trim()
    .toLowerCase();

const getRawName = (c) => {
  if (!c) return "";
  if (typeof c === "string") return c;
  if (typeof c.name === "string") return c.name;
  if (c.name && typeof c.name === "object") return c.name.es || c.name.en || "";
  return "";
};

// Heurísticas por nombre (si no viene finish)
const isMarbleByName = (name) =>
  /carrara|statuario|marquina|panda|onyx|onice|rock|venecia|luxury|gold|calacatta/i.test(
    normalizeName(name)
  );

const isStoneByName = (name) => /marfil|beige|ocre|travert/i.test(normalizeName(name));
const isMetalByName = (name) => /metal|alumin|silver|gray|gris metal/i.test(normalizeName(name));
const isSatinByName = (name) => /satin|satín|mate negro|black satin/i.test(normalizeName(name));
const isWoodByName = (name) =>
  /roble|oak|almendro|fresno|teak|nuez|tabaco|olmo|wood|cedro|nogal/i.test(normalizeName(name));

const getFinish = (c) => {
  const rawName = getRawName(c);
  if (c?.mirror) return "mirror";
  if (c?.finish) return c.finish;

  if (isMarbleByName(rawName)) return "marble";
  if (isStoneByName(rawName)) return "stone";
  if (isMetalByName(rawName)) return "metal";
  if (isSatinByName(rawName)) return "satin";
  if (isWoodByName(rawName)) return "wood";

  return "default";
};

const baseGradientFromColor = (c) => {
  if (!c || typeof c === "string") return "linear-gradient(180deg,#ccc,#ccc)";
  if (c.gradient?.stops?.length) {
    const angle = c.gradient.angle ?? 135;
    return `linear-gradient(${angle}deg, ${c.gradient.stops.join(", ")})`;
  }
  if (c.hex) return `linear-gradient(180deg, ${c.hex}, ${c.hex})`;
  return "linear-gradient(180deg,#ccc,#ccc)";
};

const bgFromColor = (c, i = 0) => {
  if (!c || typeof c === "string") return { background: "#CCCCCC" };

  const finish = getFinish(c);

  /* ================= MIRROR ================= */
  if (finish === "mirror" && c.mirror) {
    const { base, highlight, mid, shadow } = c.mirror;

    const reflection = `linear-gradient(
      180deg,
      ${highlight} 0%,
      ${mid} 22%,
      rgba(255,255,255,0.00) 40%,
      rgba(0,0,0,0.35) 70%,
      ${shadow} 100%
    )`;

    const edge = `linear-gradient(
      90deg,
      rgba(255,255,255,0.18),
      rgba(255,255,255,0.00) 12%,
      rgba(0,0,0,0.25) 88%,
      rgba(0,0,0,0.45)
    )`;

    const baseLayer = `linear-gradient(180deg, ${base}, ${base})`;

    return {
      backgroundImage: [edge, reflection, baseLayer].join(", "),
      backgroundBlendMode: "screen, overlay, normal",
      backgroundSize: "cover, cover, cover",
      backgroundPosition: "center, center, center",
    };
  }

  const base = baseGradientFromColor(c);

  /* ================= TRANSLUCENT ================= */
  if (c.translucent) {
    const { base, edge, shadow } = c.translucent;

    const glass = `linear-gradient(
      180deg,
      ${edge} 0%,
      ${base} 35%,
      rgba(255,255,255,0.18) 55%,
      ${shadow} 100%
    )`;

    const frost = `radial-gradient(
      circle at 30% 25%,
      rgba(255,255,255,0.45),
      rgba(255,255,255,0.00) 55%
    )`;

    return {
      backgroundImage: [glass, frost].join(", "),
      backgroundBlendMode: "screen, normal",
      backgroundSize: "cover, cover",
      backgroundPosition: "center, center",
      backdropFilter: "blur(2px)",
    };
  }

  /* ================= MARBLE ================= */
  if (finish === "marble") {
    const a1 = 108 + (i % 6) * 6;
    const a2 = 22 + (i % 7) * 8;

    const vDark = c.veins?.dark ?? "rgba(95,100,110,0.35)";
    const vMid = c.veins?.mid ?? "rgba(145,150,160,0.22)";

    const veins1 = `linear-gradient(
      ${a1}deg,
      rgba(0,0,0,0) 0%,
      rgba(0,0,0,0) 38%,
      ${vMid} 45%,
      rgba(0,0,0,0) 52%,
      rgba(0,0,0,0) 100%
    )`;

    const veins2 = `linear-gradient(
      ${a2}deg,
      rgba(0,0,0,0) 0%,
      rgba(0,0,0,0) 58%,
      ${vDark} 62%,
      rgba(0,0,0,0) 67%,
      rgba(0,0,0,0) 100%
    )`;

    const cloud = `radial-gradient(
      circle at 30% 25%,
      rgba(255,255,255,0.35),
      rgba(255,255,255,0.00) 58%
    )`;

    const gloss = `linear-gradient(
      180deg,
      rgba(255,255,255,0.30),
      rgba(255,255,255,0.00) 42%,
      rgba(0,0,0,0.10)
    )`;

    return {
      backgroundImage: [gloss, cloud, veins2, veins1, base].join(", "),
      backgroundBlendMode: "screen, overlay, normal, normal, normal",
      backgroundSize: "cover, cover, cover, cover, cover",
      backgroundPosition: "center, center, center, center, center",
    };
  }

  /* ================= STONE ================= */
  if (finish === "stone") {
    const pores = `radial-gradient(circle at 22% 28%, rgba(0,0,0,0.10), rgba(0,0,0,0) 38%),
                   radial-gradient(circle at 62% 40%, rgba(0,0,0,0.08), rgba(0,0,0,0) 42%),
                   radial-gradient(circle at 40% 72%, rgba(0,0,0,0.07), rgba(0,0,0,0) 44%)`;

    const softVeins = `linear-gradient(
      115deg,
      rgba(0,0,0,0) 0%,
      rgba(0,0,0,0) 44%,
      rgba(90,80,70,0.18) 52%,
      rgba(0,0,0,0) 58%,
      rgba(0,0,0,0) 100%
    )`;

    const glaze = `linear-gradient(
      180deg,
      rgba(255,255,255,0.25),
      rgba(255,255,255,0.00) 55%,
      rgba(0,0,0,0.12)
    )`;

    return {
      backgroundImage: [glaze, pores, softVeins, base].join(", "),
      backgroundBlendMode: "screen, multiply, overlay, normal",
      backgroundSize: "cover, cover, cover, cover",
      backgroundPosition: "center, center, center, center",
    };
  }

  /* ================= METAL ================= */
  if (finish === "metal") {
    const brushed = `repeating-linear-gradient(
      90deg,
      rgba(255,255,255,0.18) 0px,
      rgba(255,255,255,0.18) 1px,
      rgba(0,0,0,0.10) 2px,
      rgba(0,0,0,0.10) 4px
    )`;

    const highlight = `linear-gradient(
      180deg,
      rgba(255,255,255,0.40),
      rgba(255,255,255,0.00) 45%,
      rgba(0,0,0,0.18)
    )`;

    return {
      backgroundImage: [highlight, brushed, base].join(", "),
      backgroundBlendMode: "screen, overlay, normal",
      backgroundSize: "cover, 220% 220%, cover",
      backgroundPosition: "center, center, center",
    };
  }

  /* ================= SATIN ================= */
  if (finish === "satin") {
    const softSheen = `linear-gradient(
      180deg,
      rgba(255,255,255,0.18),
      rgba(255,255,255,0.00) 55%,
      rgba(0,0,0,0.35)
    )`;

    return {
      backgroundImage: [softSheen, base].join(", "),
      backgroundBlendMode: "screen, normal",
      backgroundSize: "cover, cover",
      backgroundPosition: "center, center",
    };
  }

  /* ================= WOOD ================= */
  if (finish === "wood") {
    const grain = `repeating-linear-gradient(
      90deg,
      rgba(255,255,255,0.08) 0px,
      rgba(255,255,255,0.08) 2px,
      rgba(0,0,0,0.10) 3px,
      rgba(0,0,0,0.10) 6px
    )`;

    const vein = `repeating-linear-gradient(
      110deg,
      rgba(0,0,0,0.12) 0px,
      rgba(0,0,0,0.12) 1px,
      rgba(255,255,255,0.06) 2px,
      rgba(255,255,255,0.06) 10px
    )`;

    const gloss = `linear-gradient(
      180deg,
      rgba(255,255,255,0.28),
      rgba(255,255,255,0.00) 40%,
      rgba(0,0,0,0.12)
    )`;

    return {
      backgroundImage: [gloss, vein, grain, base].join(", "),
      backgroundBlendMode: "screen, overlay, multiply, normal",
      backgroundSize: "cover, 180% 180%, 240% 240%, cover",
      backgroundPosition: "center, center, center, center",
    };
  }

  return { background: base };
};

/* =========================
   i18n helpers (para specsOverride)
========================= */
function isLangObj(v) {
  return v && typeof v === "object" && !Array.isArray(v) && ("es" in v || "en" in v);
}

function normalizeLang(lang) {
  return String(lang || "es").toLowerCase().startsWith("en") ? "en" : "es";
}

function pickLang(v, lang) {
  const L = normalizeLang(lang);
  if (isLangObj(v)) return v[L] ?? v.es ?? v.en ?? "";
  if (v === null || v === undefined) return "";
  if (Array.isArray(v)) return v.map((x) => pickLang(x, L)).filter(Boolean).join(", ");
  return String(v);
}

/* =========================
   Spec key normalization (para que AnchoUtil salga como usableWidth, etc.)
========================= */
const SPEC_KEY_ALIASES = {
  length: ["length", "largo", "longitud", "len"],
  width: ["width", "ancho", "anchura"],
  usableWidth: ["usablewidth", "anchoutil", "anchoutil", "ancho util", "usefulwidth", "useablewidth"],
  thickness: ["thickness", "espesor", "grosor"],
  weight: ["weight", "peso"],
  weightPerM2: ["weightperm2", "pesoporm2", "peso por m2", "peso/m2", "kg/m2"],
  loadCapacity: ["loadcapacity", "pesoresiste", "peso resiste", "carga", "kg/m²", "kg/m2"],
  coverage: ["coverage", "cubrimiento", "cobertura"],
  package: ["package", "empaque", "caja", "cajas"],
  composition: ["composition", "composicion", "composición", "material"],
  serviceLife: ["servicelife", "vidautil", "vida útil", "vidautil", "lifespan"],
  certifications: ["certifications", "certificaciones", "certificacion", "certificación"],
  installation: ["installation", "instalacion", "instalación", "montaje"],
  precautions: ["precautions", "precauciones"],
  areaPerPiece: ["areaporpieza", "area por pieza", "areaperpiece"],
  resistance: ["resistencia"],
  faces: ["caras"],
};

const normalizeSpecKey = (key) => {
  const raw = String(key || "").trim();
  const k = raw
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/\s+/g, "");

  for (const [canonical, list] of Object.entries(SPEC_KEY_ALIASES)) {
    for (const candidate of list) {
      const c = String(candidate)
        .toLowerCase()
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .replace(/\s+/g, "");
      if (k === c) return canonical;
    }
  }
  return raw;
};

const mapSpecsNormalized = (specsObj, lang) => {
  if (!specsObj || typeof specsObj !== "object") return {};
  const out = {};
  for (const [k, v] of Object.entries(specsObj)) {
    const canon = normalizeSpecKey(k);
    const val = pickLang(v, lang);
    if (String(val).trim().length === 0) continue;
    out[canon] = val;
  }
  return out;
};

export default function ProductInstalled({
  title,
  description,
  ctaText = "Cotizar",
  onCta = () => {},
  image,
  benefits = [],
  applications = [],
  specs = {},
  colors = [],
}) {
  const { t, i18n } = useTranslation();
  const lang = normalizeLang(i18n.language);

  const [imageLoaded, setImageLoaded] = useState(false);
  const imageRef = useRef(null);

  const tabs = useMemo(
    () => [
      {
        key: "beneficios",
        label: t("product.tabs.benefits", "Benefits"),
        has: benefits?.length > 0,
        icon: <Icon.Star />,
      },
      {
        key: "aplicaciones",
        label: t("product.tabs.applications", "Applications"),
        has: applications?.length > 0,
        icon: <Icon.Grid />,
      },
      {
        key: "especificaciones",
        label: t("product.tabs.specs", "Specs"),
        has: specs && Object.keys(specs).length > 0,
        icon: <Icon.Sliders />,
      },
      {
        key: "colores",
        label: t("product.tabs.colors", "Colors"),
        has: colors?.length > 0,
        icon: <Icon.Palette />,
      },
    ],
    [benefits, applications, specs, colors, t]
  );

  const [tab, setTab] = useState(() => tabs.find((tt) => tt.has)?.key || "beneficios");

  const renderList = (items) => (
    <ul className="pi-list--enhanced">
      {items.map((item, i) => (
        <li
          key={i}
          className="pi-list-item--enhanced"
          style={{ animationDelay: `${i * 0.1}s` }}
        >
          <span className="pi-bullet--enhanced" aria-hidden />
          <span>{item}</span>
        </li>
      ))}
    </ul>
  );

  /* ========= Specs normal (dl) ========= */
  const renderSpecs = (obj) => (
    <dl className="pi-specs--enhanced">
      {Object.entries(obj).map(([k, v], i) => (
        <div
          className="pi-spec-row--enhanced"
          key={k}
          style={{ animationDelay: `${i * 0.05}s` }}
        >
          <dt>{t(`product.specsLabels.${k}`, k)}</dt>
          <dd>{Array.isArray(v) ? v.join(", ") : String(v)}</dd>
        </div>
      ))}
    </dl>
  );

  /* ========= Specs por color (tabla compacta) ========= */
  const hasSpecsOverride = useMemo(() => {
    return Array.isArray(colors) && colors.some((c) => c && typeof c === "object" && c.specsOverride);
  }, [colors]);

  const colorColumns = useMemo(() => {
    if (!Array.isArray(colors)) return [];
    return colors
      .map((c) => {
        if (!c) return null;
        const name = typeof c === "string" ? c : (typeof c?.name === "string" ? c.name : pickLang(c?.name, lang));
        const overrides = mapSpecsNormalized(c?.specsOverride, lang);
        return {
          name: String(name || "").trim(),
          data: c,
          overrides,
        };
      })
      .filter((x) => x && x.name);
  }, [colors, lang]);

  const baseSpecs = useMemo(() => {
    // specs ya deberían venir “localizados”, pero por seguridad lo normalizo igual
    return mapSpecsNormalized(specs, lang);
  }, [specs, lang]);

  const compareKeys = useMemo(() => {
    if (!hasSpecsOverride || colorColumns.length === 0) return [];
    const set = new Set(Object.keys(baseSpecs));
    for (const col of colorColumns) {
      for (const k of Object.keys(col.overrides || {})) set.add(k);
    }
    return Array.from(set);
  }, [hasSpecsOverride, colorColumns, baseSpecs]);

  const renderSpecsCompare = () => {
    if (!hasSpecsOverride || colorColumns.length === 0) return renderSpecs(specs);

    // Si no hay keys (por algún motivo), caemos al render normal
    if (compareKeys.length === 0) return renderSpecs(specs);

    return (
      <div className="pi-specs-compare-wrap" style={{ overflowX: "auto" }}>
        <table className="pi-specs-compare" style={{ width: "100%", borderCollapse: "separate", borderSpacing: "0 10px" }}>
          <thead>
            <tr>
              <th
                style={{
                  textAlign: "left",
                  padding: "10px 12px",
                  whiteSpace: "nowrap",
                  position: "sticky",
                  left: 0,
                  background: "#283d57",
                  zIndex: 1,
                }}
              >
                {t("product.specsCompare.spec", "Spec")}
              </th>
              {colorColumns.map((c, idx) => (
                <th key={idx} style={{ textAlign: "left", padding: "10px 12px", whiteSpace: "nowrap" }}>
                  {c.name}
                </th>
              ))}
            </tr>
          </thead>

          <tbody>
            {compareKeys.map((k, rowIdx) => {
              const label = t(`product.specsLabels.${k}`, k);

              return (
                <tr key={k} style={{ animationDelay: `${rowIdx * 0.03}s` }}>
                  <td
                    style={{
                      padding: "12px",
                      fontWeight: 600,
                      whiteSpace: "nowrap",
                      position: "sticky",
                      left: 0,
                      background: "#283d57",
                      zIndex: 1,
                      borderRadius: "10px 0 0 10px",
                    }}
                  >
                    {label}
                  </td>

                  {colorColumns.map((col, i) => {
                    const val = col.overrides?.[k] ?? baseSpecs?.[k] ?? "";
                    const show = String(val || "").trim().length ? val : "—";
                    return (
                      <td
                        key={i}
                        style={{
                          padding: "12px",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {show}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>

        <div style={{ marginTop: 10, fontSize: 12, opacity: 0.75 }}>
          {t(
            "product.specsCompare.note",
            "Las medidas pueden variar según la variante/color."
          )}
        </div>
      </div>
    );
  };

  /* ========= Colors ========= */
  const renderColors = (arr) => (
    <div className="pi-colors--enhanced">
      {arr.map((c, i) => {
        const name = typeof c === "string" ? c : c?.name ?? "";
        return (
          <div
            key={i}
            className="pi-color--enhanced"
            style={{ animationDelay: `${i * 0.1}s` }}
          >
            <span className="pi-color-swatch--enhanced" style={bgFromColor(c, i)} />
            <div className="pi-color-info--enhanced">
              <span className="pi-color-name--enhanced">{name}</span>
            </div>
          </div>
        );
      })}
    </div>
  );

  let content = null;
  if (tab === "beneficios") content = renderList(benefits);
  if (tab === "aplicaciones") content = renderList(applications);
  if (tab === "especificaciones") content = renderSpecsCompare();
  if (tab === "colores") content = renderColors(colors);

  return (
    <section className="pi pi--enhanced">
      <div className="pi-photo pi-photo--enhanced">
        <img
          ref={imageRef}
          src={Array.isArray(image) ? image[0] : image}
          alt={title}
          loading="eager"
          onLoad={() => setImageLoaded(true)}
          className={imageLoaded ? "pi-image--loaded" : ""}
        />

        <div className="pi-overlay pi-overlay--enhanced">
          <div className="pi-content--enhanced">
            <h1 className="pi-title--enhanced">{title}</h1>
            <p className="pi-desc--enhanced">{description}</p>

            <button
              className="pi-cta pi-cta--enhanced"
              onClick={onCta}
              aria-label={t("cta.quoteAria", "Cotizar ahora")}
            >
              <Icon.ArrowRight />
              {t("cta.quote", { defaultValue: ctaText })}
            </button>
          </div>
        </div>
      </div>

      <div className="pi-panel pi-panel--enhanced">
        <div className="pi-panel-header--enhanced"></div>

        <div className="pi-tabs pi-tabs--enhanced">
          {tabs
            .filter((tt) => tt.has)
            .map((ti) => (
              <button
                key={ti.key}
                className={`pi-tab pi-tab--enhanced ${tab === ti.key ? "is-active" : ""}`}
                onClick={() => setTab(ti.key)}
              >
                {ti.icon}
                {ti.label}
              </button>
            ))}
        </div>

        {content}
      </div>
    </section>
  );
}
